/**
 * Program 12.1 UX4 — Submission Control Room wrapper (Wave 7 EST3 basis).
 */
import React, { useEffect, useMemo, useState } from 'react';
import { Alert, Button, Card, Col, Modal, Progress, Row, Space, Tag, Typography, message } from 'antd';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { epcCommercialService } from '@/services/epcCommercial.service';
import { epcEstimationService } from '@/services/epcEstimation.service';
import { formatMoney } from '@/utils/format';
import { EpcSubmissionCockpit } from './EpcSubmissionCockpit';
import { buildBidBondReadiness } from './bidBondTracker';
import { formatDeadlineDisplay } from './bidWorkspaceModel';
import { buildSubmissionReadiness } from './estimateProposalSubmissionReadiness';
import { EpcApprovedRevisionPicker } from '@/components/epc/estimation/EpcApprovedRevisionPicker';

const { Text, Title } = Typography;

export type EpcSubmissionControlPanelProps = {
  tenderId: string;
  tender: Record<string, unknown>;
  submission: Record<string, unknown> | null;
  proposals: Record<string, unknown>[];
  bidDecisions: Record<string, unknown>[];
  clarifications: Record<string, unknown>[];
  addenda: Record<string, unknown>[];
};

export const EpcSubmissionControlPanel: React.FC<EpcSubmissionControlPanelProps> = (props) => {
  const { tenderId, tender, submission, proposals, bidDecisions, clarifications, addenda } = props;
  const qc = useQueryClient();
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [certifyRevId, setCertifyRevId] = useState<string | undefined>();

  const openClar = clarifications.filter((c) => !/closed|answered/i.test(String(c.status || ''))).length;
  const pendingAddenda = addenda.filter((a) => {
    const st = String(a.status || '').toLowerCase();
    return st !== 'incorporated' && st !== 'fully_incorporated';
  }).length;
  const bondReady = buildBidBondReadiness(
    ((tender.meta_json || tender.metaJson) as Record<string, unknown> | undefined)?.bid_bond
  ).ready;

  const hasApprovedGo = bidDecisions.some((b) => {
    const outcome = String(b.outcome || '').toUpperCase();
    const status = String(b.status || '').toLowerCase();
    return status === 'approved' && (outcome === 'GO' || outcome === 'CONDITIONAL');
  });

  const linkedProposal = proposals[0] || null;
  const proposalId = linkedProposal ? String(linkedProposal.id) : '';

  const basisQ = useQuery({
    queryKey: ['epc-proposal-submission-basis', proposalId],
    queryFn: () => epcCommercialService.getProposalSubmissionBasis(proposalId),
    enabled: !!proposalId,
  });

  const readiness = submission
    ? buildSubmissionReadiness({
        submission,
        proposal: linkedProposal,
        hasApprovedGo,
        submissionDueAt: String(tender.submission_due_at || tender.submissionDueAt || '') || null,
        openClarificationCount: openClar,
        unincorporatedAddendaCount: pendingAddenda,
        bidBondReady: bondReady,
        est3SubmissionBasisStatus: String((basisQ.data as any)?.status || '') || null,
      })
    : null;

  const readyPct = readiness
    ? Math.round(
        (readiness.checks.filter((c) => c.state === 'READY').length / Math.max(readiness.checks.length, 1)) *
          100
      )
    : 0;

  const isSubmitted = submission && ['Submitted', 'Approved'].includes(String(submission.status || ''));
  const basisStatus = String((basisQ.data as any)?.status || 'NOT_RECORDED');

  const recordCustomerM = useMutation({
    mutationFn: () => {
      const rawMeta = submission!.meta_json ?? submission!.metaJson;
      let base: Record<string, unknown> = {};
      if (rawMeta && typeof rawMeta === 'object' && !Array.isArray(rawMeta)) {
        base = { ...(rawMeta as Record<string, unknown>) };
      } else if (typeof rawMeta === 'string' && rawMeta.trim()) {
        try {
          const parsed = JSON.parse(rawMeta) as unknown;
          if (parsed && typeof parsed === 'object' && !Array.isArray(parsed)) {
            base = parsed as Record<string, unknown>;
          }
        } catch {
          base = {};
        }
      }
      return epcCommercialService.patchSubmission(String(submission!.id), {
        metaJson: {
          ...base,
          customer_submission: {
            method: 'Portal',
            submitted_at: new Date().toISOString(),
            reference: `PORTAL-${Date.now().toString().slice(-6)}`,
          },
        },
      });
    },
    onSuccess: () => {
      message.success('Customer submission recorded');
      qc.invalidateQueries({ queryKey: ['epc-tender-workspace', tenderId] });
      setConfirmOpen(false);
    },
    onError: (e: Error) => message.error(e.message || 'Failed to record customer submission'),
  });

  const certifyM = useMutation({
    mutationFn: (payload: {
      estimate_id: string;
      estimate_revision_id: string;
      replace?: boolean;
    }) => epcCommercialService.certifyProposalEstimateBasis(proposalId, payload),
    onSuccess: () => {
      message.success('Estimate basis certified');
      qc.invalidateQueries({ queryKey: ['epc-tender-workspace', tenderId] });
      qc.invalidateQueries({ queryKey: ['epc-proposal-submission-basis', proposalId] });
    },
    onError: (e: Error) => message.error(e.message || 'Certify failed'),
  });

  const warnings = readiness?.blockers.filter((b) => b.severity === 'warning') || [];
  const blockers = readiness?.blockers.filter((b) => b.severity === 'blocking') || [];

  const pin = useMemo(() => {
    const meta = (linkedProposal?.meta_json || linkedProposal?.metaJson || {}) as Record<string, unknown>;
    return (meta.est3_pricing_pin || meta.est3PricingPin || null) as Record<string, unknown> | null;
  }, [linkedProposal]);

  const estimateIdForCert = String(
    linkedProposal?.estimation_id || linkedProposal?.estimationId || pin?.estimate_id || ''
  );

  const estimatesQ = useQuery({
    queryKey: ['epc-estimates', tenderId],
    queryFn: () => epcEstimationService.list(tenderId),
    enabled: !!tenderId && !!proposalId && !isSubmitted,
  });
  const resolveEstimateId =
    estimateIdForCert || (estimatesQ.data?.[0]?.id ? String(estimatesQ.data[0].id) : '');

  const wsQ = useQuery({
    queryKey: ['epc-estimate-ws', resolveEstimateId],
    queryFn: () => epcEstimationService.getWorkspace(resolveEstimateId),
    enabled: !!resolveEstimateId && !!proposalId && !isSubmitted,
  });

  useEffect(() => {
    const pinRev = String(pin?.estimate_revision_id || '');
    if (pinRev) setCertifyRevId(pinRev);
    else {
      const approved = (wsQ.data?.revisions || []).find(
        (r: any) => String(r.status || '').toLowerCase() === 'approved'
      );
      if (approved) setCertifyRevId(String(approved.id));
    }
  }, [pin?.estimate_revision_id, wsQ.data?.revisions]);

  return (
    <Space direction="vertical" style={{ width: '100%' }} size="middle" id="submission">
      <Card>
        <Title level={4} style={{ marginTop: 0 }}>
          SUBMISSION CONTROL
        </Title>
        <Row gutter={[16, 8]}>
          <Col xs={12} md={6}>
            <Text type="secondary">Tender</Text>
            <div>{String(tender.tender_number || '—')}</div>
          </Col>
          <Col xs={12} md={6}>
            <Text type="secondary">Deadline</Text>
            <div>{formatDeadlineDisplay(tender.submission_due_at || tender.submissionDueAt)}</div>
          </Col>
          <Col xs={12} md={6}>
            <Text type="secondary">Bid price</Text>
            <div>
              {linkedProposal?.commercial_value != null
                ? formatMoney(Number(linkedProposal.commercial_value), {
                    currency: String(linkedProposal.currency || 'AED'),
                  })
                : '—'}
            </div>
          </Col>
          <Col xs={12} md={6}>
            <Text type="secondary">EST3 submission basis</Text>
            <div>
              <Tag color={basisStatus === 'CERTIFIED' ? 'green' : basisStatus === 'STALE' ? 'red' : 'orange'}>
                {basisStatus === 'NOT_RECORDED' ? 'NOT RECORDED' : basisStatus}
              </Tag>
            </div>
          </Col>
        </Row>
        <Progress percent={readyPct} style={{ marginTop: 12 }} format={() => `Pack checklist ${readyPct}%`} />
      </Card>

      {basisStatus === 'STALE' && (
        <Alert
          type="error"
          showIcon
          message="Submission basis STALE"
          description={(basisQ.data as any)?.stale_reason || 'Re-certify after reviewing tender addenda.'}
        />
      )}

      {proposalId && !isSubmitted && (
        <Card size="small" title="Certify EST3 estimate basis">
          <Space direction="vertical" style={{ width: '100%' }} size="small">
            <EpcApprovedRevisionPicker
              revisions={(wsQ.data?.revisions || []) as Array<Record<string, unknown>>}
              value={certifyRevId}
              onChange={setCertifyRevId}
            />
            <Button
              type="primary"
              loading={certifyM.isPending}
              disabled={!resolveEstimateId || !certifyRevId}
              onClick={() => {
                if (!resolveEstimateId || !certifyRevId) {
                  message.warning('Select an Approved revision');
                  return;
                }
                Modal.confirm({
                  title: 'Certify submission basis?',
                  content:
                    'Pin revision as the immutable estimate basis for customer submission.',
                  onOk: () =>
                    certifyM.mutateAsync({
                      estimate_id: resolveEstimateId,
                      estimate_revision_id: certifyRevId,
                      replace: !!pin && String(pin.estimate_revision_id) !== certifyRevId,
                    }),
                });
              }}
            >
              Certify estimate basis
            </Button>
          </Space>
        </Card>
      )}

      {blockers.length > 0 && (
        <Alert
          type="error"
          showIcon
          message="BLOCKERS"
          description={
            <ul style={{ margin: 0, paddingLeft: 18 }}>
              {blockers.map((b) => (
                <li key={b.id}>{b.reason}</li>
              ))}
            </ul>
          }
        />
      )}

      {warnings.length > 0 && (
        <Alert
          type="warning"
          showIcon
          message="WARNINGS"
          description={warnings.map((w) => w.reason).join(' · ')}
        />
      )}

      <EpcSubmissionCockpit
        {...props}
        openClarificationCount={openClar}
        unincorporatedAddendaCount={pendingAddenda}
        bidBondReady={bondReady}
      />

      {isSubmitted && String(submission!.status) === 'Approved' && (
        <Card size="small" title="Customer submission">
          <Button type="primary" onClick={() => setConfirmOpen(true)}>
            Record customer submitted
          </Button>
          <Modal
            open={confirmOpen}
            title="Confirm customer submission recorded?"
            onCancel={() => setConfirmOpen(false)}
            onOk={() => recordCustomerM.mutate()}
            confirmLoading={recordCustomerM.isPending}
          >
            Marks portal / customer submission metadata on this pack.
          </Modal>
        </Card>
      )}
    </Space>
  );
};
